import { useState, useEffect, useRef } from 'react';
import { cleanForTTS } from '../utils/cleanForTTS';
import { synthesizeSpeech } from '../services/backendAPI';
import { splitIntoSentences } from '../utils/splitIntoSentences';

/**
 * Manages all text-to-speech state and the speak() function.
 * Supports Browser, ElevenLabs, and OpenAI TTS engines.
 *
 * ttsEngine, elVoiceId, openaiVoiceId, voiceOut are controlled externally
 * (lifted to AppContext) so the demo nav can drive them.
 *
 * @param {object} opts
 * @param {string}   opts.selectedBackend  - current backend key (used to derive TTS port)
 * @param {boolean}  opts.voiceOut         - when false, speak() is a no-op
 * @param {string}   opts.ttsEngine        - 'browser' | 'elevenlabs' | 'openai' | 'qwen'
 * @param {Function} opts.setTtsEngine
 * @param {string}   opts.elVoiceId        - ElevenLabs voice ID
 * @param {Function} opts.setElVoiceId
 * @param {string}   opts.openaiVoiceId    - OpenAI voice name
 * @param {Function} opts.setOpenaiVoiceId
 * @param {string}   opts.qwenVoiceId      - Qwen voice name
 * @param {Function} opts.setQwenVoiceId
 * @param {Function} opts.log              - (type, msg) => void
 */
export function useTTS({ selectedBackend, voiceOut, ttsEngine, setTtsEngine, elVoiceId, setElVoiceId, openaiVoiceId, setOpenaiVoiceId, qwenVoiceId, setQwenVoiceId, log }) {
  const [isSpeaking,        setIsSpeaking]        = useState(false);
  const [availableVoices,   setAvailableVoices]   = useState([]);
  const [selectedVoiceName, setSelectedVoiceName] = useState('');

  const voiceRef          = useRef(null);
  const currentAudioRef   = useRef(null);
  const ttsEngineRef      = useRef(ttsEngine);
  const elVoiceIdRef      = useRef(elVoiceId);
  const openaiVoiceIdRef  = useRef(openaiVoiceId);
  const qwenVoiceIdRef    = useRef(qwenVoiceId);
  const selectedBackendRef = useRef(selectedBackend);

  // Keep refs current on every render so async closures read the latest values
  // --- helper for cancellation: when a clip ends, check if we're still meant to be playing
  const isCancelled = () => !currentAudioRef.current;

  ttsEngineRef.current      = ttsEngine;
  elVoiceIdRef.current      = elVoiceId;
  openaiVoiceIdRef.current  = openaiVoiceId;
  qwenVoiceIdRef.current    = qwenVoiceId;
  selectedBackendRef.current = selectedBackend;

  // Load available English voices and auto-select the best one
  useEffect(() => {
    const synth = window.speechSynthesis;
    if (!synth) return;
    const loadVoices = () => {
      const all = synth.getVoices();
      const en  = all.filter(v => v.lang.startsWith('en'));
      if (!en.length) return;
      setAvailableVoices(en);
      const priority = [
        v => /google us english/i.test(v.name),
        v => /microsoft.*natural/i.test(v.name),
        v => /google uk english female/i.test(v.name),
        v => /enhanced/i.test(v.name) && v.lang === 'en-US',
        v => /enhanced/i.test(v.name) && v.lang.startsWith('en'),
        v => /google/i.test(v.name) && v.lang.startsWith('en'),
        v => v.lang === 'en-US' && v.localService === false,
        v => v.lang === 'en-US',
        v => v.lang.startsWith('en'),
      ];
      let best = null;
      for (const test of priority) { best = en.find(test); if (best) break; }
      if (best && !voiceRef.current) {
        voiceRef.current = best;
        setSelectedVoiceName(best.name);
      }
    };
    synth.addEventListener('voiceschanged', loadVoices);
    loadVoices();
    return () => synth.removeEventListener('voiceschanged', loadVoices);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync voiceRef when user picks a different browser voice
  useEffect(() => {
    if (!selectedVoiceName) return;
    const v = window.speechSynthesis?.getVoices().find(v => v.name === selectedVoiceName);
    if (v) voiceRef.current = v;
  }, [selectedVoiceName]);

  const cancelSpeech = () => {
    window.speechSynthesis?.cancel();
    if (currentAudioRef.current) { currentAudioRef.current.pause(); currentAudioRef.current = null; }
    setIsSpeaking(false);
    log('tts', 'TTS cancelled by user');
  };

  const speak = async (text) => {
    if (!voiceOut) { log('tts', 'Voice output muted — skipping TTS'); return; }
    const clean = cleanForTTS(text);
    if (!clean) return;

    window.speechSynthesis?.cancel();
    if (currentAudioRef.current) { currentAudioRef.current.pause(); currentAudioRef.current = null; }

    const playFromBackend = async (engineLabel, ttsOpts) => {
      setIsSpeaking(true);
      try {
        const blob = await synthesizeSpeech({ ...ttsOpts });
        const url  = URL.createObjectURL(blob);
        const audio = new Audio(url);
        currentAudioRef.current = audio;
        audio.onended = () => { setIsSpeaking(false); URL.revokeObjectURL(url); currentAudioRef.current = null; log('tts', `${engineLabel} finished`); };
        audio.onerror = () => { setIsSpeaking(false); URL.revokeObjectURL(url); currentAudioRef.current = null; log('error', `${engineLabel} playback error`); };
        await audio.play();
        log('tts', `${engineLabel} playing`);
      } catch (e) {
        setIsSpeaking(false);
        log('error', `${engineLabel} TTS failed: ${e.message} — falling back to browser`);
        const utt = new SpeechSynthesisUtterance(clean);
        if (voiceRef.current) utt.voice = voiceRef.current;
        utt.onend   = () => setIsSpeaking(false);
        utt.onerror = () => setIsSpeaking(false);
        window.speechSynthesis?.speak(utt);
      }
    };

    if (ttsEngineRef.current === 'elevenlabs') {
      log('tts', `ElevenLabs → ${clean.length} chars`);
      await playFromBackend('ElevenLabs', { provider: 'elevenlabs', text: clean, voiceId: elVoiceIdRef.current });
    } else if (ttsEngineRef.current === 'openai') {
      log('tts', `OpenAI → ${clean.length} chars`);
      await playFromBackend('OpenAI', { provider: 'openai', text: clean, voice: openaiVoiceIdRef.current });
    } else if (ttsEngineRef.current === 'qwen') {
      log('tts', `Qwen → ${clean.length} chars, voice: ${qwenVoiceIdRef.current || 'ryan'}`);
      // Sentence-level chunking for lower perceived latency.
      const sentences = splitIntoSentences(clean);
      log('tts', `Qwen → ${sentences.length} chunks`);
      // Pre-fetch all chunks in parallel, then play them sequentially.
      setIsSpeaking(true);
      const clips = await Promise.all(
        sentences.map(s => synthesizeSpeech({ provider: 'qwen', text: s, voice: qwenVoiceIdRef.current || 'ryan' })
          .then(blob => URL.createObjectURL(blob))
          .catch(() => null)),
      );
      for (const url of clips.filter(Boolean)) {
        if (!currentAudioRef.current) break; // cancelled mid-stream
        await new Promise((resolve) => {
          const audio = new Audio(url);
          currentAudioRef.current = audio;
          audio.onended   = () => { URL.revokeObjectURL(url); resolve(); log('tts', 'Qwen chunk done'); };
          audio.onerror   = ()  => { URL.revokeObjectURL(url); resolve(); log('error', `Qwen chunk playback failed url=${url}`); };
          audio.play().catch(resolve);
        });
      }
      currentAudioRef.current = null;
      setIsSpeaking(false);
      log('tts', 'Qwen finished all chunks');
    } else {
      if (!window.speechSynthesis) return;
      const utt = new SpeechSynthesisUtterance(clean);
      if (voiceRef.current) utt.voice = voiceRef.current;
      utt.rate  = 1.0;
      utt.pitch = 1.0;
      utt.onstart = () => { setIsSpeaking(true);  log('tts', 'Browser TTS started'); };
      utt.onend   = () => { setIsSpeaking(false); log('tts', 'Browser TTS finished'); };
      utt.onerror = (e) => { setIsSpeaking(false); log('error', `Browser TTS error: ${e.error}`); };
      window.speechSynthesis.speak(utt);
      log('tts', `Browser TTS → ${clean.length} chars`);
    }
  };

  return {
    ttsEngine, setTtsEngine,
    elVoiceId, setElVoiceId,
    openaiVoiceId, setOpenaiVoiceId,
    qwenVoiceId, setQwenVoiceId,
    isSpeaking,
    availableVoices,
    selectedVoiceName, setSelectedVoiceName,
    currentAudioRef,
    speak,
    cancelSpeech,
  };
}
